import React, { useState, useEffect, useCallback } from 'react';
import api, { getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, ListChecks, RefreshCw, Layers, X } from 'lucide-react';

const SCOPE_TYPES = [
  { value: 'GLOBAL', label: 'Global (all shifts)' },
  { value: 'PARTICIPANT', label: 'Participant' },
  { value: 'HOUSE', label: 'SIL House' },
  { value: 'CENTRE_SESSION', label: 'Centre Session' },
  { value: 'SHIFT_TYPE', label: 'Shift Type' },
];

const SCOPE_BADGE = {
  GLOBAL: 'bg-slate-600',
  PARTICIPANT: 'bg-blue-600',
  HOUSE: 'bg-emerald-600',
  CENTRE_SESSION: 'bg-violet-600',
  SHIFT_TYPE: 'bg-amber-600',
};

const OPTION_KEY = {
  PARTICIPANT: 'participants',
  HOUSE: 'houses',
  CENTRE_SESSION: 'centreSessions',
  SHIFT_TYPE: 'shiftTypes',
};

const emptyForm = { name: '', scopeType: 'GLOBAL', scopeId: '', scopeLabel: '', items: [], active: true };

function TemplateFormDialog({ open, onClose, template, options, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (template) {
        setForm({
          name: template.name || '',
          scopeType: template.scopeType || 'GLOBAL',
          scopeId: template.scopeId || '',
          scopeLabel: template.scopeLabel || '',
          items: (template.items || []).map((i) => ({ label: i.label, category: i.category || 'OTHER' })),
          active: template.active !== false,
        });
      } else {
        setForm({ ...emptyForm, items: [] });
      }
    }
  }, [open, template]);

  const categories = options?.categories || [];
  const targets = form.scopeType !== 'GLOBAL' ? (options?.[OPTION_KEY[form.scopeType]] || []) : [];

  const updateItem = (idx, field, val) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [field]: val } : it)) }));
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { label: '', category: 'OTHER' }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const seedDefaults = () => {
    const def = (options?.defaultItems || []).map((d) => ({ label: d.label, category: d.category || 'OTHER' }));
    setForm((f) => ({ ...f, items: def }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Template name is required'); return; }
    if (form.scopeType !== 'GLOBAL' && !form.scopeId) { toast.error('Select a target for this scope'); return; }
    const items = form.items.filter((i) => (i.label || '').trim());
    if (items.length === 0) { toast.error('Add at least one checklist item'); return; }

    let scopeLabel = form.scopeLabel;
    if (form.scopeType !== 'GLOBAL') {
      const t = targets.find((o) => String(o.id) === String(form.scopeId));
      scopeLabel = t?.label || form.scopeLabel;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        scopeType: form.scopeType,
        scopeId: form.scopeType === 'GLOBAL' ? null : form.scopeId,
        scopeLabel,
        items,
        active: form.active,
      };
      if (template) {
        await api.put(`/mobile/admin/task-templates/${template.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/mobile/admin/task-templates', payload);
        toast.success('Template created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-template-dialog">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Task Template' : 'New Task Template'}</DialogTitle>
          <DialogDescription>
            Configure a reusable checklist. Matching templates auto-generate the shift checklist for support workers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Template name *</Label>
            <Input
              data-testid="template-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. SIL Morning Routine"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Applies to *</Label>
              <Select
                value={form.scopeType}
                onValueChange={(v) => setForm({ ...form, scopeType: v, scopeId: '', scopeLabel: '' })}
              >
                <SelectTrigger data-testid="template-scope-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCOPE_TYPES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.scopeType !== 'GLOBAL' && (
              <div className="space-y-2">
                <Label>Target *</Label>
                <Select value={form.scopeId} onValueChange={(v) => setForm({ ...form, scopeId: v })}>
                  <SelectTrigger data-testid="template-scope-target"><SelectValue placeholder="Select target" /></SelectTrigger>
                  <SelectContent>
                    {targets.length === 0 && <SelectItem value="__none" disabled>No options available</SelectItem>}
                    {targets.map((o) => (
                      <SelectItem key={o.id} value={String(o.id)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Checklist items *</Label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={seedDefaults} data-testid="seed-defaults-btn">
                  <Layers className="h-3.5 w-3.5 mr-1" /> Use defaults
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add item
                </Button>
              </div>
            </div>

            {form.items.length === 0 && (
              <p className="text-sm text-muted-foreground py-3 text-center border rounded-md">
                No items yet. Add items or start from defaults.
              </p>
            )}

            <div className="space-y-2">
              {form.items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`item-row-${idx}`}>
                  <Input
                    className="flex-1"
                    value={it.label}
                    onChange={(e) => updateItem(idx, 'label', e.target.value)}
                    placeholder="Task label"
                    data-testid={`item-label-${idx}`}
                  />
                  <Select value={it.category} onValueChange={(v) => updateItem(idx, 'category', v)}>
                    <SelectTrigger className="w-40" data-testid={`item-category-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="template-active-switch" />
            <Label className="cursor-pointer">Active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cancel-template-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
            {saving ? 'Saving…' : template ? 'Save changes' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MobileTaskTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, oRes] = await Promise.all([
        api.get('/mobile/admin/task-templates'),
        api.get('/mobile/admin/task-templates/options'),
      ]);
      setTemplates(tRes.data?.data || []);
      setOptions(oRes.data?.data || null);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/mobile/admin/task-templates/${deleteTarget.id}`);
      toast.success('Template deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setDialogOpen(true); };

  return (
    <div className="p-6 space-y-6" data-testid="task-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ListChecks className="h-6 w-6" /> Task Templates
          </h1>
          <p className="text-muted-foreground">
            Configure reusable shift checklists by participant, SIL house, centre session, shift type, or globally.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-templates-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openNew} data-testid="new-template-btn">
            <Plus className="h-4 w-4 mr-2" /> New Template
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Loading templates…</div>
          ) : templates.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground" data-testid="templates-empty">
              No task templates yet. Create one to auto-generate shift checklists.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Applies to</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((t) => (
                  <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell>
                      <Badge className={`${SCOPE_BADGE[t.scopeType] || 'bg-slate-600'} text-white`}>
                        {SCOPE_TYPES.find((s) => s.value === t.scopeType)?.label || t.scopeType}
                      </Badge>
                      {t.scopeLabel && <span className="ml-2 text-sm text-muted-foreground">{t.scopeLabel}</span>}
                    </TableCell>
                    <TableCell>{t.items?.length || 0}</TableCell>
                    <TableCell>
                      <Badge variant={t.active ? 'default' : 'secondary'}>{t.active ? 'Active' : 'Inactive'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(t)} data-testid={`delete-template-${t.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TemplateFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        template={editing}
        options={options}
        onSaved={load}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete task template?"
        description={`"${deleteTarget?.name}" will be removed. Existing shift checklists are not affected.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
}
